import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  MapPin,
  Landmark,
  LogIn,
  UserPlus,
  Heart,
  Share2,
  Menu,
  X,
  LogOut,
  ChevronDown,
  LayoutDashboard,
  UserRound,
  Compass,
  ArrowRight,
  UtensilsCrossed,
  BedDouble,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import UserAvatar from "./UserAvatar";
import { useSettings } from "../contexts/SettingsContext";
import { resolveAssetDisplayUrl } from "../lib/assetUrl";
import { useAppContext } from "../store/AppContext";
import FavoritePlaceCard, { FavoriteEmptyState } from "./FavoritePlaceCard";
import AdminJobsTray from "./admin/AdminJobsTray";
import { useToast } from "../contexts/ToastContext";

const cls = (...args) => args.filter(Boolean).join(" ");

const BrandIcon = ({ children, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

const FacebookBrandIcon = ({ className = "" }) => (
  <BrandIcon className={className}>
    <path
      d="M14.5 8.75V7.1c0-.8.35-1.35 1.45-1.35H17V3h-1.8C12.9 3 11.75 4.35 11.75 6.55v2.2H10V11.8h1.75V21h2.75v-9.2h2.05l.35-3.05H14.5Z"
      fill="currentColor"
    />
  </BrandIcon>
);

const ThreadsBrandIcon = ({ className = "" }) => (
  <BrandIcon className={className}>
    <path
      d="M12 2.6c-4.95 0-8.97 4.02-8.97 8.97 0 4.64 3.55 8.44 8.1 8.93v-3.1c-2.72-.47-4.77-2.78-4.77-5.83 0-3.4 2.32-6.18 5.64-6.18 2.54 0 4.31 1.52 5.1 3.68-.92-.58-2.04-.9-3.3-.9-2.64 0-4.5 1.4-4.5 3.5 0 1.95 1.5 3.23 3.77 3.23 1.35 0 2.55-.42 3.48-1.18-.33 1.78-1.56 3.16-3.39 3.61v3.2c3.86-.55 6.83-3.84 6.83-8.07 0-4.95-4.02-8.97-8.97-8.97Zm.07 10.36c-.9 0-1.5-.43-1.5-1.12 0-.76.75-1.3 1.85-1.3 1.1 0 2.02.43 2.64 1.14-.72.83-1.75 1.28-2.99 1.28Z"
      fill="currentColor"
    />
  </BrandIcon>
);

const MessengerBrandIcon = ({ className = "" }) => (
  <BrandIcon className={className}>
    <path
      d="M12 3c-4.97 0-9 3.73-9 8.33 0 2.62 1.31 4.96 3.36 6.5V21l3.06-1.69c.85.24 1.75.37 2.68.37 4.97 0 9-3.73 9-8.35C21 6.73 16.97 3 12 3Z"
      fill="currentColor"
      opacity="0.95"
    />
    <path
      d="M7.4 13.7 10.2 9.2c.4-.64 1.28-.8 1.88-.35l2.1 1.6c.17.13.4.14.58.03l2.85-1.78c.38-.24.82.22.57.6l-2.8 4.5c-.4.64-1.28.8-1.88.35l-2.1-1.6a.5.5 0 0 0-.58-.03L8 14.3c-.38.24-.82-.22-.6-.6Z"
      fill="#fffaf1"
    />
  </BrandIcon>
);

const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://longvongdalat.com"
).replace(/\/+$/, "");

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { user, isAuthenticated, logout } = useAuth();
  const { settings } = useSettings();
  const { favorites = [], locations = [], toggleFavorite } = useAppContext();
  const { push: pushToast } = useToast();

  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, loc: null });

  const userMenuRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const isMapPage = location.pathname === "/map";
  const isLocationsPage = location.pathname === "/locations";

  const activeCategory = useMemo(() => {
    if (location.pathname !== "/locations") return "";

    try {
      const p = new URLSearchParams(location.search || "");
      return String(p.get("category") || "").trim().toLowerCase();
    } catch {
      return "";
    }
  }, [location.pathname, location.search]);

  const isFoodPage = isLocationsPage && activeCategory === "am-thuc-local";
  const isStayPage = isLocationsPage && activeCategory === "homestay-khach-san";

  const siteName = (settings?.siteName || "").trim() || "Lòng Vòng Đà Lạt";
  const logoSrc = resolveAssetDisplayUrl(
    settings?.logo?.url,
    settings?.logo?.key
  );

  const favoriteItems = useMemo(() => {
    const ids = Array.isArray(favorites) ? favorites.map(String) : [];
    if (!ids.length) return [];

    const locationMap = new Map(
      (Array.isArray(locations) ? locations : []).map((item) => [
        String(item.id),
        item,
      ])
    );

    return ids.map((id) => locationMap.get(id)).filter(Boolean);
  }, [favorites, locations]);

  const handleLogout = () => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setFavoritesOpen(false);
    logout();
  };

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setFavoritesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.hash) return;

    const sectionId = location.hash.replace("#", "");
    const t = setTimeout(() => scrollToSection(sectionId), 150);

    return () => clearTimeout(t);
  }, [location]);

  useEffect(() => {
    if (!userMenuOpen) return;

    const onDoc = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen || favoritesOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, favoritesOpen]);

  const openFavorites = () => {
    setFavoritesOpen(true);
    setUserMenuOpen(false);
    setMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen((prev) => !prev);
    setFavoritesOpen(false);
  };

  const handleExploreNavigate = () => {
    setMenuOpen(false);
    navigate("/map");
  };

  const navPillClass = (active, mobile = false) =>
    cls(
      mobile
        ? "flex min-h-[40px] min-w-0 flex-1 cursor-pointer select-none items-center justify-center gap-1 rounded-full px-1.5 py-1.5 text-[11px] font-bold leading-tight sm:min-h-[44px] sm:gap-1.5 sm:px-2 sm:text-xs md:text-sm"
        : "inline-flex cursor-pointer select-none items-center gap-2 rounded-full px-5 py-3 text-sm font-bold",
      "transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6a7d62] focus-visible:ring-offset-2",
      active
        ? "border border-[#5f745f] bg-[#5f745f] text-white shadow-[0_8px_18px_rgba(95,116,95,0.28)]"
        : "text-[#5d6758] hover:bg-white hover:text-[#2f352c] active:scale-[0.98]"
    );

  const copyToClipboardFallback = async (text) => {
    const t = String(text || "");
    if (!t) return false;

    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = t;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";

        document.body.appendChild(ta);
        ta.select();

        const ok = document.execCommand("copy");
        document.body.removeChild(ta);

        return Boolean(ok);
      } catch {
        return false;
      }
    }
  };

  const shareLocation = (loc) => {
    const slug = String(loc?.slug || "").trim();
    if (!slug) return;

    const url = `${SITE_URL}/location/${encodeURIComponent(slug)}`;

    setShareModal({
      open: true,
      loc: {
        ...loc,
        __shareUrl: url,
      },
    });
  };

  const openPopup = (u) => {
    try {
      window.open(
        String(u),
        "_blank",
        "noopener,noreferrer,width=680,height=520"
      );
    } catch {
      // ignore
    }
  };

  const renderFavoriteList = () => {
    if (!favorites.length) {
      return (
        <FavoriteEmptyState
          compact
          onExplore={() => {
            setFavoritesOpen(false);
            navigate("/map");
          }}
        />
      );
    }

    if (!favoriteItems.length) {
      return (
        <div className="rounded-2xl border border-[#e0d2bc] bg-[#fffaf0] px-4 py-5 text-sm text-[#5d6558]">
          Đang tải danh sách địa điểm…
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {favoriteItems.slice(0, 50).map((loc) => (
          <FavoritePlaceCard
            key={loc.id}
            loc={loc}
            onOpen={() => {
              setFavoritesOpen(false);
              navigate(`/location/${encodeURIComponent(loc.slug)}`);
            }}
            onShowOnMap={() => {
              setFavoritesOpen(false);
              navigate(`/map?locationId=${encodeURIComponent(loc.id)}`);
            }}
            onShare={shareLocation}
            onRemove={() => toggleFavorite?.(loc.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <header className="relative z-[10000] border-b border-[#d8cdbb] bg-[linear-gradient(180deg,rgba(103,121,97,0.96)_0%,rgba(92,108,88,0.96)_100%)] shadow-[0_10px_30px_rgba(54,48,35,0.16)] backdrop-blur-xl">
        <div className="mx-auto w-full max-w-[1800px] px-4 md:px-6 xl:px-8">
          <div className="relative flex min-h-[56px] items-center justify-between gap-1.5 py-2 md:h-[84px] md:min-h-0 md:gap-4 md:py-0 lg:min-h-0">
            {/* LEFT - LOGO */}
            <Link
              to="/"
              className="group relative z-10 flex min-w-0 shrink-0 items-center gap-0 lg:gap-3"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e9dfce] bg-white shadow-[0_6px_18px_rgba(40,30,20,0.12)] ring-1 ring-black/5 sm:h-12 sm:w-12 md:h-14 md:w-14">
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={siteName}
                    className="h-full w-full rounded-full object-cover"
                    loading="eager"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <MapPin
                    size={22}
                    className="text-[#5f745f]"
                    strokeWidth={2}
                  />
                )}
              </div>

              <div className="hidden min-w-0 leading-tight lg:block">
                <p className="truncate font-faustina text-lg font-bold italic tracking-tight text-[#fff9ef] md:text-[24px]">
                  {siteName}
                </p>
                <p className="hidden text-[11px] font-semibold uppercase tracking-[0.24em] text-[#edf3e8]/65 sm:block md:text-xs">
                  Khám phá thành phố sương mù
                </p>
              </div>
            </Link>

            {/* MOBILE / TABLET NAV */}
            <div className="min-w-0 flex-1 px-0.5 lg:hidden">
              <nav
                className="flex w-full max-w-full items-center gap-0.5 rounded-full border border-[#d7cab6] bg-white p-0.5 shadow-[0_8px_18px_rgba(60,45,30,0.10)] ring-1 ring-black/5 sm:p-1"
                aria-label="Điều hướng chính"
              >
                <button
                  type="button"
                  onClick={handleExploreNavigate}
                  className={navPillClass(isMapPage, true)}
                  aria-current={isMapPage ? "page" : undefined}
                  aria-label="Bản đồ"
                  title="Bản đồ"
                >
                  <Compass size={16} strokeWidth={2} className="shrink-0" />
                  <span className="hidden min-w-0 truncate sm:inline">
                    Bản đồ
                  </span>
                </button>

                <Link
                  to="/locations?category=du-lich-van-hoa"
                  onClick={() => setMenuOpen(false)}
                  className={navPillClass(
                    isLocationsPage && !isFoodPage && !isStayPage,
                    true
                  )}
                  aria-current={
                    isLocationsPage && !isFoodPage && !isStayPage
                      ? "page"
                      : undefined
                  }
                  aria-label="VĂN HOÁ"
                  title="VĂN HOÁ"
                >
                  <MapPin size={16} strokeWidth={2} className="shrink-0" />
                  <span className="hidden min-w-0 truncate sm:inline">
                    ĐIỂM VĂN HOÁ
                  </span>
                </Link>

                <Link
                  to="/locations?category=am-thuc-local"
                  onClick={() => setMenuOpen(false)}
                  className={navPillClass(isFoodPage, true)}
                  aria-current={isFoodPage ? "page" : undefined}
                  aria-label="ĂN/UỐNG"
                  title="ĂN/UỐNG"
                >
                  <UtensilsCrossed
                    size={16}
                    strokeWidth={2}
                    className="shrink-0"
                  />
                  <span className="hidden min-w-0 truncate sm:inline">
                    ĂN/UỐNG
                  </span>
                </Link>

                <Link
                  to="/locations?category=homestay-khach-san"
                  onClick={() => setMenuOpen(false)}
                  className={navPillClass(isStayPage, true)}
                  aria-current={isStayPage ? "page" : undefined}
                  aria-label="HOMESTAY"
                  title="HOMESTAY"
                >
                  <BedDouble
                    size={16}
                    strokeWidth={2}
                    className="shrink-0"
                  />
                  <span className="hidden min-w-0 truncate sm:inline">
                    HOMESTAY
                  </span>
                </Link>
              </nav>
            </div>

            {/* CENTER - DESKTOP NAV */}
            <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg:block">
              <nav
                className="inline-flex items-center gap-0.5 rounded-full border border-[#d9d9d9] bg-white p-1 shadow-[0_10px_24px_rgba(60,45,30,0.10)] ring-1 ring-black/5"
                aria-label="Điều hướng chính"
              >
                <button
                  type="button"
                  onClick={handleExploreNavigate}
                  className={navPillClass(isMapPage)}
                  aria-current={isMapPage ? "page" : undefined}
                >
                  <Compass size={17} strokeWidth={2} />
                  <span>Bản đồ</span>
                </button>

                <Link
                  to="/locations?category=du-lich-van-hoa"
                  className={navPillClass(
                    isLocationsPage && !isFoodPage && !isStayPage
                  )}
                  aria-current={
                    isLocationsPage && !isFoodPage && !isStayPage
                      ? "page"
                      : undefined
                  }
                >
                  <Landmark size={17} strokeWidth={2} />
                  <span>VĂN HOÁ</span>
                </Link>

                <Link
                  to="/locations?category=am-thuc-local"
                  className={navPillClass(isFoodPage)}
                  aria-current={isFoodPage ? "page" : undefined}
                >
                  <UtensilsCrossed size={17} strokeWidth={2} />
                  <span>ĂN/UỐNG</span>
                </Link>

                <Link
                  to="/locations?category=homestay-khach-san"
                  className={navPillClass(isStayPage)}
                  aria-current={isStayPage ? "page" : undefined}
                >
                  <BedDouble size={17} strokeWidth={2} />
                  <span>HOMESTAY</span>
                </Link>
              </nav>
            </div>

            {/* RIGHT - DESKTOP ACTIONS */}
            <div className="relative z-10 ml-auto hidden items-center gap-3 md:flex">
              {isAdmin ? <AdminJobsTray variant="site" size="lg" /> : null}

              <button
                type="button"
                onClick={openFavorites}
                className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#e9d6d6] bg-[#fff8f8] text-[#a85d5d] shadow-[0_8px_18px_rgba(60,45,30,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#d9b7b7] hover:bg-[#fff1f1]"
                title="Yêu thích"
                aria-label="Yêu thích"
              >
                <Heart size={20} className="fill-current text-[#a85d5d]" />

                {favorites.length ? (
                  <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#a85d5d] px-1 text-[11px] font-extrabold text-white shadow-md">
                    {favorites.length > 99 ? "99+" : favorites.length}
                  </span>
                ) : null}
              </button>

              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={toggleUserMenu}
                    className={cls(
                      "inline-flex h-14 items-center gap-3 rounded-full border px-3 pr-4 shadow-[0_8px_18px_rgba(60,45,30,0.08)] transition-all duration-300",
                      userMenuOpen
                        ? "border-[#d7cab6] bg-white shadow-[0_12px_26px_rgba(60,45,30,0.12)] ring-1 ring-black/5"
                        : "border-[#e1d4c0] bg-white hover:border-[#cdbd9f] hover:shadow-[0_12px_26px_rgba(60,45,30,0.12)]"
                    )}
                    aria-expanded={userMenuOpen}
                    aria-haspopup="menu"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-[#d7cab6]">
                      <UserAvatar
                        url={user?.avatar}
                        name={user?.fullName || user?.email}
                        sizeClass="h-10 w-10"
                        textClass="text-xs font-bold"
                        roundedClass="rounded-full"
                        imgClassName=""
                      />
                    </div>

                    <div className="hidden min-w-0 text-left lg:block">
                      <p className="max-w-[120px] truncate text-sm font-bold text-[#2f352c]">
                        {user?.fullName || "Tài khoản"}
                      </p>
                      <p className="max-w-[120px] truncate text-xs text-[#7b7569]">
                        {user?.username || user?.email || ""}
                      </p>
                    </div>

                    <ChevronDown
                      size={16}
                      className={cls(
                        "text-[#7b7569] transition-transform duration-300",
                        userMenuOpen ? "rotate-180" : ""
                      )}
                    />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-[calc(100%+14px)] z-[100000] w-[320px] overflow-hidden rounded-[28px] border border-[#d9d9d9] bg-white shadow-[0_24px_60px_rgba(0,0,0,0.18)]">
                      <div className="bg-[linear-gradient(180deg,#697961_0%,#556652_100%)] p-5 text-white">
                        <div className="flex items-center gap-3">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-white/20">
                            <UserAvatar
                              url={user?.avatar}
                              name={user?.fullName || user?.email}
                              sizeClass="h-14 w-14"
                              textClass="text-sm font-bold"
                              roundedClass="rounded-full"
                              imgClassName=""
                            />
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-base font-bold">
                              {user?.fullName || "Tài khoản"}
                            </p>
                            <p className="truncate text-sm text-white/75">
                              {user?.email || user?.username || ""}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-3">
                        <div className="space-y-2">
                          {isAdmin ? (
                            <Link
                              to="/admin/dashboard"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 rounded-2xl border border-[#e2d6c5] bg-white px-4 py-3 text-sm font-semibold text-[#535b4d] transition hover:bg-[#f7f1e6]"
                            >
                              <LayoutDashboard
                                size={18}
                                className="text-[#7a7467]"
                              />
                              <span>Dashboard</span>
                            </Link>
                          ) : null}

                          <Link
                            to="/tai-khoan"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 rounded-2xl border border-[#e2d6c5] bg-white px-4 py-3 text-sm font-semibold text-[#535b4d] transition hover:bg-[#f7f1e6]"
                          >
                            <UserRound
                              size={18}
                              className="text-[#7a7467]"
                            />
                            <span>Tài khoản</span>
                          </Link>
                        </div>

                        <div className="mt-3 border-t border-[#eee5d8] pt-3">
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="flex w-full items-center justify-between rounded-2xl bg-[#fff1f1] px-4 py-3 text-sm font-bold text-[#a85d5d] transition hover:bg-[#ffe8e8]"
                          >
                            <span className="inline-flex items-center gap-3">
                              <LogOut size={18} />
                              Đăng xuất
                            </span>
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="inline-flex h-12 items-center gap-2 rounded-full px-4 text-sm font-semibold text-[#fdf9ef] transition hover:bg-white/10 hover:text-white"
                  >
                    <LogIn size={18} strokeWidth={1.8} />
                    <span>Đăng nhập</span>
                  </Link>

                  <Link
                    to="/register"
                    className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-5 text-sm font-bold text-[#556652] shadow-[0_10px_20px_rgba(60,45,30,0.12)] transition hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_14px_28px_rgba(60,45,30,0.16)]"
                  >
                    <UserPlus size={18} strokeWidth={1.8} />
                    <span>Đăng ký</span>
                  </Link>
                </div>
              )}
            </div>

            {/* MOBILE ACTIONS */}
            <div className="relative z-10 flex shrink-0 items-center gap-1.5 md:hidden sm:gap-2">
              {isAdmin ? <AdminJobsTray variant="site" size="md" /> : null}

              <button
                type="button"
                onClick={openFavorites}
                className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#ead8d8] bg-[#fff8f8] text-[#a85d5d] shadow-[0_6px_14px_rgba(60,45,30,0.08)] transition hover:border-[#d9b7b7] hover:bg-[#fff1f1]"
                aria-label="Yêu thích"
              >
                <Heart size={20} className="fill-current text-[#a85d5d]" />

                {favorites.length ? (
                  <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#a85d5d] px-1 text-[11px] font-extrabold text-white shadow-md">
                    {favorites.length > 99 ? "99+" : favorites.length}
                  </span>
                ) : null}
              </button>

              <button
                type="button"
                onClick={() => {
                  setMenuOpen((v) => !v);
                  setFavoritesOpen(false);
                  setUserMenuOpen(false);
                }}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/15"
                aria-label="Mở menu"
              >
                {menuOpen ? (
                  <X size={20} strokeWidth={2} />
                ) : (
                  <Menu size={20} strokeWidth={2} />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER OVERLAY */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[100000] bg-black/45 backdrop-blur-[2px] md:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* MOBILE DRAWER */}
      <div
        className={cls(
          "fixed right-0 top-0 z-[100001] h-full w-[320px] max-w-[88vw] bg-[linear-gradient(180deg,#65785f_0%,#586955_100%)] shadow-2xl transition-all duration-300 md:hidden",
          menuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/60">
              Menu
            </p>
            <p className="mt-1 text-base font-bold text-white">{siteName}</p>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Đóng menu"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="flex h-[calc(100%-84px)] flex-col overflow-y-auto px-4 py-5">
          {isAuthenticated && (
            <div className="mb-4 flex items-center gap-3 rounded-[26px] border border-white/10 bg-white/10 p-4">
              <UserAvatar
                url={user?.avatar}
                name={user?.fullName || user?.email}
                sizeClass="h-11 w-11"
                textClass="text-xs font-bold"
                roundedClass="rounded-full"
                imgClassName=""
              />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">
                  {user?.fullName || "Tài khoản"}
                </p>
                <p className="truncate text-xs text-white/65">
                  {user?.email || user?.username || ""}
                </p>
              </div>
            </div>
          )}

          <nav
            className="flex w-full items-center gap-0.5 rounded-full border border-[#d9d9d9] bg-white p-1 shadow-[0_10px_22px_rgba(40,30,20,0.14)]"
            aria-label="Điều hướng chính"
          >
            <button
              type="button"
              onClick={handleExploreNavigate}
              className={navPillClass(isMapPage, true)}
              aria-current={isMapPage ? "page" : undefined}
              aria-label="Bản đồ"
              title="Bản đồ"
            >
              <Compass size={16} strokeWidth={2} className="shrink-0" />
              <span className="hidden sm:inline">Bản đồ</span>
            </button>

            <Link
              to="/locations?category=du-lich-van-hoa"
              onClick={() => setMenuOpen(false)}
              className={navPillClass(
                isLocationsPage && !isFoodPage && !isStayPage,
                true
              )}
              aria-current={
                isLocationsPage && !isFoodPage && !isStayPage
                  ? "page"
                  : undefined
              }
              aria-label="VĂN HOÁ"
              title="VĂN HOÁ"
            >
              <MapPin size={16} strokeWidth={2} className="shrink-0" />
              <span className="hidden sm:inline">VĂN HOÁ</span>
            </Link>

            <Link
              to="/locations?category=am-thuc-local"
              onClick={() => setMenuOpen(false)}
              className={navPillClass(isFoodPage, true)}
              aria-current={isFoodPage ? "page" : undefined}
              aria-label="ĂN/UỐNG"
              title="ĂN/UỐNG"
            >
              <UtensilsCrossed
                size={16}
                strokeWidth={2}
                className="shrink-0"
              />
              <span className="hidden sm:inline">ĂN/UỐNG</span>
            </Link>

            <Link
              to="/locations?category=homestay-khach-san"
              onClick={() => setMenuOpen(false)}
              className={navPillClass(isStayPage, true)}
              aria-current={isStayPage ? "page" : undefined}
              aria-label="HOMESTAY"
              title="HOMESTAY"
            >
              <BedDouble size={16} strokeWidth={2} className="shrink-0" />
              <span className="hidden sm:inline">HOMESTAY</span>
            </Link>
          </nav>

          <div className="my-5 h-px bg-white/10" />

          {isAuthenticated ? (
            <div className="space-y-2">
              {isAdmin ? (
                <Link
                  to="/admin/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/90 transition hover:bg-white/10"
                >
                  <LayoutDashboard size={18} />
                  Dashboard
                </Link>
              ) : null}

              <Link
                to="/tai-khoan"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/90 transition hover:bg-white/10"
              >
                <UserRound size={18} />
                Tài khoản
              </Link>

              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-[22px] border border-white/10 bg-white/5 px-4 py-3 text-left text-sm font-medium text-white/90 transition hover:bg-rose-500/15 hover:text-rose-200"
              >
                <LogOut size={18} />
                Đăng xuất
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 rounded-[22px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white/90 transition hover:bg-white/10"
              >
                <LogIn size={18} />
                Đăng nhập
              </Link>

              <Link
                to="/register"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2.5 rounded-[22px] bg-white px-4 py-3.5 text-sm font-bold text-[#4d5c48] shadow-lg transition hover:opacity-95"
              >
                <UserPlus size={18} />
                Đăng ký tài khoản
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* FAVORITES SIDE PANEL */}
      <div
        className={cls(
          "fixed inset-0 z-[100010] transition-all duration-300",
          favoritesOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
      >
        <div
          className={cls(
            "absolute inset-0 bg-black/45 backdrop-blur-[2px] transition-opacity duration-300",
            favoritesOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setFavoritesOpen(false)}
        />

        <aside
          className={cls(
            "absolute right-0 top-0 flex h-full w-full max-w-[420px] flex-col bg-[#f7f1e6] shadow-[0_20px_80px_rgba(0,0,0,0.28)] transition-transform duration-300",
            favoritesOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="border-b border-[#d9ccb4] bg-[linear-gradient(180deg,#697961_0%,#556652_100%)] px-5 py-5 text-white">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/65">
                  Danh sách yêu thích
                </p>
                <h3 className="mt-1 font-faustina text-2xl font-bold italic text-[#fff9ef]">
                  {favorites.length || 0} địa điểm đã lưu
                </h3>
                <p className="mt-1 text-sm text-white/75">
                  Truy cập nhanh các địa điểm bạn quan tâm.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setFavoritesOpen(false)}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white transition hover:bg-white/20"
                aria-label="Đóng danh sách yêu thích"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-[#f7f1e6] px-4 py-4">
            {renderFavoriteList()}
          </div>

          <div className="border-t border-[#d9ccb4] bg-[#fffaf0] p-4">
            <button
              type="button"
              disabled={!favorites.length}
              onClick={() => {
                setFavoritesOpen(false);
                navigate("/map?fav=1");
              }}
              className="flex w-full items-center justify-center gap-2 rounded-[22px] bg-[#5f745f] px-4 py-3.5 text-sm font-bold text-white shadow-md transition hover:bg-[#4f6250] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Xem trên bản đồ
              <ArrowRight size={16} />
            </button>
          </div>
        </aside>
      </div>

      {/* SHARE CHOOSER */}
      {shareModal.open ? (
        <div
          className="fixed inset-0 z-[100020] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
          onClick={() => setShareModal({ open: false, loc: null })}
          role="dialog"
          aria-modal="true"
          aria-label="Chia sẻ địa điểm"
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-[26px] border border-[#d9d9d9] bg-white shadow-[0_24px_60px_rgba(0,0,0,0.20)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-[#d9d9d9] bg-white px-5 py-4">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#7d8374]">
                  Chia sẻ
                </p>
                <p className="mt-1 truncate text-base font-extrabold text-[#2f352c]">
                  {shareModal.loc?.name || "Địa điểm"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShareModal({ open: false, loc: null })}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#dfd4c4] bg-white text-[#6b6559] transition hover:bg-[#f7f1e6]"
                aria-label="Đóng"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    const url = shareModal.loc?.__shareUrl;
                    const ok = await copyToClipboardFallback(url);

                    setShareModal({ open: false, loc: null });
                    setFavoritesOpen(false);

                    window.setTimeout(() => {
                    pushToast(
                      ok
                        ? "Đã copy link!"
                        : "Không copy được link. Bạn thử lại nhé.",
                      ok ? "success" : "error"
                    );
                    }, 50);
                  }}
                  className="group flex w-full items-center gap-3 rounded-[22px] border border-[#e2d6c5] bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-[#f7f1e6] hover:shadow-[0_10px_22px_rgba(60,45,30,0.10)] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6a7d62] focus-visible:ring-offset-2"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#e8f2f2] text-[#3a6665] ring-1 ring-black/5 transition group-hover:scale-[1.03]">
                    <Share2 size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-[#2f352c]">
                      Copy link
                    </span>
                    <span className="block truncate text-xs font-semibold text-[#7b7569]">
                      Dán vào chat/bài viết
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const url = shareModal.loc?.__shareUrl;
                    const name = String(shareModal.loc?.name || "").trim();
                    if (!url) return;

                    openPopup(
                      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        url
                      )}&quote=${encodeURIComponent(
                        `${name ? `${name} — ` : ""}${url}`.trim()
                      )}`
                    );

                    setShareModal({ open: false, loc: null });
                  }}
                  className="group flex w-full items-center gap-3 rounded-[22px] border border-[#e2d6c5] bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-[#f7f1e6] hover:shadow-[0_10px_22px_rgba(60,45,30,0.10)] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6a7d62] focus-visible:ring-offset-2"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#eaf2ff] text-[#1877F2] ring-1 ring-black/5 transition group-hover:scale-[1.03]">
                    <FacebookBrandIcon className="text-[#1877F2]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-[#2f352c]">
                      Facebook
                    </span>
                    <span className="block truncate text-xs font-semibold text-[#7b7569]">
                      Chia sẻ lên Facebook
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const url = shareModal.loc?.__shareUrl;
                    const name = String(shareModal.loc?.name || "").trim();
                    const text = `${name ? `${name} — ` : ""}${url || ""}`.trim();

                    openPopup(
                      `https://www.threads.net/intent/post?text=${encodeURIComponent(
                        text
                      )}`
                    );

                    setShareModal({ open: false, loc: null });
                  }}
                  className="group flex w-full items-center gap-3 rounded-[22px] border border-[#e2d6c5] bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-[#f7f1e6] hover:shadow-[0_10px_22px_rgba(60,45,30,0.10)] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6a7d62] focus-visible:ring-offset-2"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f0f0f0] text-[#111] ring-1 ring-black/5 transition group-hover:scale-[1.03]">
                    <ThreadsBrandIcon className="text-[#111]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-[#2f352c]">
                      Threads
                    </span>
                    <span className="block truncate text-xs font-semibold text-[#7b7569]">
                      Đăng lên Threads
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    const url = shareModal.loc?.__shareUrl;
                    const ok = await copyToClipboardFallback(url);

                    try {
                      window.open(
                        "https://www.messenger.com/new",
                        "_blank",
                        "noopener,noreferrer"
                      );
                    } catch {
                      // ignore
                    }

                    setShareModal({ open: false, loc: null });
                    setFavoritesOpen(false);

                    window.setTimeout(() => {
                    pushToast(
                      ok
                        ? "Đã copy link. Messenger đã mở — dán link vào chat để gửi."
                        : "Messenger đã mở. Bạn copy link thủ công rồi gửi nhé.",
                      ok ? "success" : "info"
                    );
                    }, 50);
                  }}
                  className="group flex w-full items-center gap-3 rounded-[22px] border border-[#e2d6c5] bg-white px-4 py-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-[#f7f1e6] hover:shadow-[0_10px_22px_rgba(60,45,30,0.10)] active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6a7d62] focus-visible:ring-offset-2"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#e9f3ff] text-[#1b74e4] ring-1 ring-black/5 transition group-hover:scale-[1.03]">
                    <MessengerBrandIcon className="text-[#1b74e4]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-[#2f352c]">
                      Messenger
                    </span>
                    <span className="block truncate text-xs font-semibold text-[#7b7569]">
                      Gửi qua Messenger
                    </span>
                  </span>
                </button>
              </div>

              <p className="mt-3 text-xs font-semibold text-[#7b7569]">
                Mẹo: với Messenger tụi mình copy link sẵn để bạn dán vào cuộc trò chuyện.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Header;